import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { useCallback, useEffect, useMemo } from "react";
import {
  fetchRecruitmentAreaById,
  removeRecruitmentArea,
  updateRecruitmentArea,
} from "./recruitmentsSlice";
import { Nullable } from "../../../common/types/nullable";
import { RecruitmentsAreaForm } from "./RecruitmentsAreaForm";
import {
  ChangeRecruitmentArea,
  RecruitmentAreaDetails,
  RecruitmentAreaForm,
} from "./types";
import LoadingSpinner from "../../../common/components/functional/loadingSpinner/LoadingSpinner";
import { FormContainer } from "../../customer/customerManagement/styles";
import { selectAllExpertUsers } from "../../user/expertUsersSlice";
import { getCoreUserFullName } from "../../../common/utils/users";
import { formatCitiesToNameIdOptions } from "../helpers";
import { Heading1 } from "../../../common/components/styled/styled";
import ArrowLeft from "../../../common/icons/arrowLeft.svg";
import { Box, Grid } from "@mui/material";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import { useTranslation } from "react-i18next";
import { getIds } from "../../../app/common";
import { isFulfilled } from "@reduxjs/toolkit";
import { FormProvider, useForm } from "react-hook-form";

type Props = {
  id: string;
  close: () => void;
};

export const RecruitmentAreaDetailsComponent = ({ id, close }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const expertUsers = useAppSelector(selectAllExpertUsers);
  const { data } = useAppSelector(
    (state) => state.recruitments.recruitmentAreaDetails,
  );

  useEffect(() => {
    if (id) dispatch(fetchRecruitmentAreaById({ id }));
  }, [dispatch, id]);

  const formatDefaultValues = useCallback(
    (
      data: Nullable<RecruitmentAreaDetails>,
    ): RecruitmentAreaForm | undefined => {
      if (!data) return undefined;
      const { id, version, members, cities, ...formData } = data;

      const formattedMembers = expertUsers
        .filter((expertUser) => members.includes(expertUser.id))
        .map((expertUser) => ({
          id: expertUser.id,
          name: getCoreUserFullName(expertUser),
        }));

      const formattedCities = formatCitiesToNameIdOptions(cities);

      return {
        ...formData,
        members: formattedMembers,
        cities: formattedCities,
      };
    },
    [expertUsers],
  );

  const defaultValues: RecruitmentAreaForm | undefined = useMemo(
    () => formatDefaultValues(data),
    [data, formatDefaultValues],
  );

  const methods = useForm<RecruitmentAreaForm>({
    defaultValues,
    mode: "onChange",
  });

  useEffect(() => {
    methods.reset(defaultValues);
  }, [methods, defaultValues]);

  const deleteRecruitmentArea = () => {
    dispatch(removeRecruitmentArea({ id: data?.id as string, version: 1 }));
    close();
  };

  const onSubmit = (formData: RecruitmentAreaForm) => {
    const payload: ChangeRecruitmentArea = {
      id: data?.id as string,
      version: data?.version as number,
      name: formData.name,
      experts: getIds(formData.members),
      cities: getIds(formData.cities),
    };
    dispatch(updateRecruitmentArea(payload)).then((action) => {
      if (isFulfilled(action)) close();
    });
  };

  return (
    <FormContainer maxWidth="930px" minHeight="328px" position="relative">
      {defaultValues ? (
        <>
          <Box display="flex" alignItems="center">
            <Heading1>
              <img
                src={ArrowLeft}
                alt={"< Back: "}
                style={{ marginRight: "16px", cursor: "pointer" }}
                onClick={close}
              />
              {defaultValues.name}
            </Heading1>
            <SpinnerButton
              label={t("delete")}
              onClick={deleteRecruitmentArea}
              color="error"
              sx={{ height: "36px", borderRadius: "20px", marginLeft: "16px" }}
            />
          </Box>
          <FormProvider {...methods}>
            <Grid container rowSpacing={1} columnSpacing={2}>
              <RecruitmentsAreaForm
                onSubmit={onSubmit}
                close={close}
                labelSize={1.5}
                fieldSize={10.5}
              />
            </Grid>
          </FormProvider>
        </>
      ) : (
        <LoadingSpinner
          sx={{
            position: "absolute",
            maxHeight: "calc(100% - 32px)",
            maxWidth: "calc(100% - 32px)",
          }}
        />
      )}
    </FormContainer>
  );
};
